import { watch, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import Swal from 'sweetalert2'

/**
 * Injeta (uma única vez) os estilos e a animação do velocímetro usado no
 * aviso de lentidão. Mantido fora do Swal.fire para não recriar a cada disparo.
 */
const OVERLAY_TIMEOUT_STYLE_ID = 'velta-overlay-timeout-style'

const injectOverlayTimeoutStyles = () => {
    if (document.getElementById(OVERLAY_TIMEOUT_STYLE_ID)) return

    const style = document.createElement('style')
    style.id = OVERLAY_TIMEOUT_STYLE_ID
    style.textContent = `
        .velta-slow-popup {
            border-radius: 1.25rem !important;
            padding-bottom: 1.75rem !important;
            box-shadow: 0 24px 60px -12px rgba(115, 103, 240, 0.35) !important;
        }
        .velta-slow-title {
            font-size: 1.35rem !important;
            font-weight: 700 !important;
            color: #5e5873;
        }
        .dark-layout .velta-slow-title { color: #d0d2d6; }

        .velta-slow-illustration {
            position: relative;
            width: 168px;
            height: 116px;
            margin: 0.25rem auto 1.25rem;
        }
        .velta-slow-illustration .glow {
            position: absolute;
            left: 50%;
            bottom: 8px;
            width: 130px;
            height: 130px;
            transform: translateX(-50%);
            background: radial-gradient(circle, rgba(115,103,240,0.28) 0%, rgba(115,103,240,0) 68%);
            border-radius: 50%;
            animation: veltaGlowPulse 2.4s ease-in-out infinite;
        }
        .velta-slow-illustration svg { position: relative; display: block; }

        .velta-gauge-needle {
            transform-origin: 100px 100px;
            animation: veltaNeedleSweep 2.6s cubic-bezier(0.65, 0, 0.35, 1) infinite;
        }
        .velta-gauge-hub { animation: veltaHubPulse 2.4s ease-in-out infinite; }

        .velta-slow-dots {
            display: inline-flex;
            gap: 6px;
            margin-left: 6px;
            vertical-align: middle;
        }
        .velta-slow-dots span {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #7367f0;
            display: inline-block;
            animation: veltaDotBounce 1.4s ease-in-out infinite;
        }
        .velta-slow-dots span:nth-child(2) { animation-delay: 0.2s; }
        .velta-slow-dots span:nth-child(3) { animation-delay: 0.4s; }

        .velta-slow-text {
            font-size: 1rem;
            line-height: 1.55;
            color: #6e6b7b;
            max-width: 26rem;
            margin: 0 auto;
        }
        .dark-layout .velta-slow-text { color: #b4b7bd; }
        .velta-slow-text strong { color: #7367f0; font-weight: 600; }

        .velta-slow-reassure {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin-top: 1rem;
            padding: 0.4rem 0.9rem;
            border-radius: 999px;
            background: rgba(40, 199, 111, 0.12);
            color: #28c76f;
            font-size: 0.82rem;
            font-weight: 600;
        }

        @keyframes veltaNeedleSweep {
            0%   { transform: rotate(-82deg); }
            50%  { transform: rotate(78deg); }
            100% { transform: rotate(-82deg); }
        }
        @keyframes veltaHubPulse {
            0%, 100% { r: 8px; opacity: 1; }
            50%      { r: 10px; opacity: 0.85; }
        }
        @keyframes veltaGlowPulse {
            0%, 100% { opacity: 0.55; transform: translateX(-50%) scale(0.9); }
            50%      { opacity: 1;    transform: translateX(-50%) scale(1.08); }
        }
        @keyframes veltaDotBounce {
            0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
            30%           { transform: translateY(-5px); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
            .velta-gauge-needle,
            .velta-gauge-hub,
            .velta-slow-illustration .glow,
            .velta-slow-dots span { animation: none !important; }
            .velta-gauge-needle { transform: rotate(20deg); }
        }
    `
    document.head.appendChild(style)
}

/**
 * HTML do conteúdo do aviso: velocímetro animado + mensagem acolhedora.
 */
const buildSlowOverlayHtml = () => `
    <div class="velta-slow-illustration">
        <span class="glow"></span>
        <svg width="168" height="116" viewBox="0 0 200 130" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="veltaGaugeArc" x1="20" y1="100" x2="180" y2="100" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stop-color="#28c76f"/>
                    <stop offset="0.55" stop-color="#7367f0"/>
                    <stop offset="1" stop-color="#ea5455"/>
                </linearGradient>
            </defs>
            <path d="M22 100 A78 78 0 0 1 178 100" stroke="#ebe9f1" stroke-width="14" stroke-linecap="round"/>
            <path d="M22 100 A78 78 0 0 1 178 100" stroke="url(#veltaGaugeArc)" stroke-width="14" stroke-linecap="round" opacity="0.9"/>
            <g stroke="#fff" stroke-width="2">
                <line x1="100" y1="24" x2="100" y2="34"/>
                <line x1="49" y1="42" x2="56" y2="49"/>
                <line x1="151" y1="42" x2="144" y2="49"/>
                <line x1="30" y1="100" x2="40" y2="100"/>
                <line x1="170" y1="100" x2="160" y2="100"/>
            </g>
            <g class="velta-gauge-needle">
                <path d="M100 100 L100 40" stroke="#5e5873" stroke-width="5" stroke-linecap="round"/>
            </g>
            <circle class="velta-gauge-hub" cx="100" cy="100" r="8" fill="#7367f0"/>
            <circle cx="100" cy="100" r="3.5" fill="#fff"/>
        </svg>
    </div>
    <p class="velta-slow-text">
        Os dados estão demorando para chegar porque <strong>a sua conexão com a internet está lenta ou instável</strong>.
        Confira o Wi-Fi ou o cabo de rede e, se puder, teste em outra rede (como o 4G do celular).
        <span class="velta-slow-dots"><span></span><span></span><span></span></span>
    </p>
    <div class="velta-slow-reassure">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 3l7 3v5c0 4.5-3 8.3-7 9.5C8 22.3 5 18.5 5 14V6l7-3z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
        </svg>
        Seus dados estão seguros
    </div>
`

export default function overlayTimeout(view) {
    let overlayTimer = null

    const clearOverlayTimer = () => {
        if (overlayTimer) {
            clearTimeout(overlayTimer)
            overlayTimer = null
        } else {
            // Fecha o Swal se estiver aberto
            if (Swal.isVisible()) {
                Swal.close()
            }
        }
    }

    const startOverlayTimer = () => {
        clearOverlayTimer()

        overlayTimer = setTimeout(() => {
            injectOverlayTimeoutStyles()

            Swal.fire({
                title: 'Sua internet está lenta',
                html: buildSlowOverlayHtml(),
                showCancelButton: true,
                confirmButtonText: 'Recarregar a página',
                cancelButtonText: 'Aguardar mais um pouco',
                reverseButtons: true,
                showClass: { popup: 'animate__animated animate__fadeInUp animate__faster' },
                hideClass: { popup: 'animate__animated animate__fadeOutDown animate__faster' },
                customClass: {
                    popup: 'velta-slow-popup',
                    title: 'velta-slow-title',
                    confirmButton: 'btn btn-primary ml-1 my-2',
                    cancelButton: 'btn btn-outline-primary',
                },
                buttonsStyling: false,
            }).then((result) => {
                if (result.value) {
                    store.commit(`${view}/setOverlay`, false);

                    window.location.reload();
                } else {
                    startOverlayTimer()
                }
            })
        }, 25000)
    }

    watch(
        () => store.state[view]?.overlay,
        (newValue) => {
            if (newValue) {
                startOverlayTimer()
            } else {
                clearOverlayTimer()
            }
        }
    )

    // Limpa o timer quando o componente é destruído
    onUnmounted(() => {
        clearOverlayTimer()
    })
}
